import React, { FC, JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { EvSpinner, EvTitle, TableSortOption, useSkipLinks } from '@evinced-private/ui-common';

import { NoDataIndicationError } from 'src/components/no-data-indication-error/NoDataIndicationError';

import { EvPropertyViews } from '../../components/common/ev-property-views/EvPropertyViews';
import {
	getDefaultSort,
	getTableDefinition
} from '../../components/common-pages-breakdown-table/CommonPagesBreakdownTableHelper';
import { ScanTableDefinition } from '../../components/scan-analysis/base/BaseScanViewV2';
import { formatShortMonthDatetime } from '../../helpers/DateFormatHelper';
import { RoutesHelper } from '../../helpers/RoutesHelper';
import { IScan } from '../../interfaces/Scan';
import {
	INavigationRoute,
	useNavigation
} from '../../providers/navigationProvider/NavigationProvider';
import { IPropertyViewContext, usePropertyViewContext } from '../../providers/PropertyViewProvider';
import { logger } from '../../services/Logger';
import {
	getCommonPagesIssues,
	ICompareScansPagesPaginationResponse
} from '../../services/scan/ScansComparisonService';
import { getCachedScansMetadata } from '../../services/scan/ScansService';
import { FilterParams } from '../../types/FilterParams';
import { PaginationParams } from '../../types/PaginationParams';

import { TableWithFilters } from './components/TableWithFilters';

import './ScansComparisonPages.scss';

const defaultSort: TableSortOption = getDefaultSort();

export const CommonPagesPage: FC = () => {
	const history = useHistory();
	const [isPageLoading, setPageLoading] = useState(true);
	const [commonPagesResponse, setCommonPagesResponse] =
		useState<ICompareScansPagesPaginationResponse>(null);
	const [error, setError] = useState(false);
	const [totalCountWithoutFilter, setTotalCountWithoutFilter] = useState<number>();
	const [baseScan, setBaseScan] = useState<IScan>();
	const [comparableScan, setComparableScan] = useState<IScan>();
	const { setCurrentRoute } = useNavigation();
	const queryParams: URLSearchParams = useMemo(
		() => new URLSearchParams(history.location.search),
		[history]
	);
	const baseScanId: string = queryParams.get(RoutesHelper.COMPARE_SCANS_PARAMS.BASE_SCAN_ID);
	const comparableScanId: string = queryParams.get(
		RoutesHelper.COMPARE_SCANS_PARAMS.COMPARABLE_SCAN_ID
	);
	const { propertyId } = useParams<{ propertyId: string }>();

	const propertyViewContext: IPropertyViewContext = usePropertyViewContext();
	const currentViewId: string = useMemo(
		() => propertyViewContext?.currentView?.id,
		[propertyViewContext]
	);

	const screenId: string = useMemo(() => {
		return `scan-compare-view-${currentViewId}-${baseScanId}-${comparableScanId}-common-pages`;
	}, [baseScanId, comparableScanId, currentViewId]);

	useSkipLinks(false);

	const initialLoad = useCallback(async () => {
		setPageLoading(true);
		if (!baseScanId || !comparableScanId) {
			setError(true);
			logger.error(
				`Scan comparison page - baseScanId and/or comparableScanId parameters werent available in the url ${history.location.pathname}`
			);
			return;
		}
		try {
			const [scans, responseWithoutFilters] = await Promise.all([
				getCachedScansMetadata(propertyId, currentViewId),
				getCommonPagesIssues({
					baseScanId,
					comparableScanId,
					viewId: currentViewId
				})
			]);
			setTotalCountWithoutFilter(responseWithoutFilters.totalResults);
			const base: IScan = scans.find((scan) => scan.id === baseScanId);
			const comparable: IScan = scans.find((scan) => scan.id === comparableScanId);
			setBaseScan(base);
			setComparableScan(comparable);
			setPageLoading(false);
		} catch (err) {
			setError(true);
			logger.error(
				`Scan comparison page - error loading scans metadata for property ${propertyId}`,
				err
			);
		}
	}, [baseScanId, comparableScanId, propertyId, currentViewId, history.location.pathname]);

	useEffect(() => {
		initialLoad();
	}, [initialLoad]);

	const currentRoute: INavigationRoute = useMemo(() => {
		if (!propertyId || !baseScan || !comparableScan) {
			return null;
		}

		return {
			id: propertyId,
			title: 'Common Pages List'
		};
	}, [propertyId, baseScan, comparableScan]);

	useEffect(() => {
		if (currentRoute) {
			setCurrentRoute(currentRoute);
		}
		return () => {
			setCurrentRoute(null);
		};
	}, [currentRoute, setCurrentRoute]);

	const loadDataForTable = useCallback(
		async (
			baseScanId: string,
			comparableScanId: string,
			paginationParams: PaginationParams,
			filtersParams: FilterParams
		): Promise<void> => {
			try {
				const paginationResponse: ICompareScansPagesPaginationResponse = await getCommonPagesIssues(
					{
						baseScanId,
						comparableScanId,
						paginationParams,
						filtersParams,
						viewId: currentViewId
					}
				);
				setCommonPagesResponse(paginationResponse);
			} catch (err) {
				setError(true);
				logger.error(
					`Scan comparison page - error loading issues for base: ${baseScanId}, comparable: ${comparableScanId}`,
					err
				);
			}
		},
		[currentViewId]
	);

	const navigationHeader: JSX.Element = useMemo(
		() => (
			<div className="page-navigation-headers">
				<EvTitle titleText="Common Pages List" className="title" />
				{baseScan && comparableScan && (
					<div className="scan-dates">
						<span className="scan-date">{formatShortMonthDatetime(baseScan.createdTime)}</span>
						<span className="vs">&nbsp;vs.&nbsp;</span>
						<span className="scan-date">
							{formatShortMonthDatetime(comparableScan.createdTime)}
						</span>
					</div>
				)}
			</div>
		),
		[baseScan, comparableScan]
	);

	const tableDefinition: ScanTableDefinition = useMemo(() => {
		return (
			commonPagesResponse &&
			getTableDefinition(
				commonPagesResponse.pages,
				totalCountWithoutFilter,
				commonPagesResponse.totalResults
			)
		);
	}, [commonPagesResponse, totalCountWithoutFilter]);

	if (error) {
		return (
			<div className="page-content">
				<NoDataIndicationError />
			</div>
		);
	}

	if (isPageLoading) {
		return (
			<div className="page-content">
				<EvSpinner />
			</div>
		);
	}

	return (
		<div className="page-content">
			<div className="common-pages-header-container with-breadcrumbs">
				{navigationHeader}
				<EvPropertyViews propertyId={propertyId} />
			</div>

			<TableWithFilters
				defaultTableSort={defaultSort}
				tableId={screenId}
				filtersId={screenId}
				baseScanId={baseScanId}
				comparableScanId={comparableScanId}
				tableDefinition={tableDefinition}
				loadTableItems={loadDataForTable}
			/>
		</div>
	);
};
